import React from 'react';
import { UtensilsCrossed, Briefcase, Dumbbell, ShoppingBag, ArrowRight } from 'lucide-react';
import { tenantMix } from '../data/mock';

const iconMap = {
  UtensilsCrossed,
  Briefcase,
  Dumbbell,
  ShoppingBag
};

const TenantMixSection = () => {
  return (
    <section id="tenants" className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-emerald-600 font-semibold text-sm tracking-wider uppercase mb-4 block">
            Curated Tenant Mix
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Diverse Offerings for Every Need
          </h2>
          <p className="text-slate-600 text-lg max-w-3xl mx-auto leading-relaxed">
            Our tenant strategy focuses on complementary businesses that drive foot traffic,
            serve community needs, and create a vibrant retail destination.
          </p>
        </div>

        {/* Tenant Categories */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {tenantMix.map((tenant, index) => {
            const IconComponent = iconMap[tenant.icon];
            const gradients = [
              'from-emerald-500 to-teal-600',
              'from-slate-700 to-slate-900',
              'from-amber-500 to-orange-600',
              'from-cyan-500 to-blue-600'
            ];
            
            return (
              <div
                key={tenant.id}
                className="group relative overflow-hidden rounded-2xl bg-white border border-slate-100 hover:border-transparent hover:shadow-2xl transition-all duration-500"
              >
                <div className="p-8 relative z-10">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${gradients[index]} flex items-center justify-center mb-6 shadow-lg`}>
                    <IconComponent className="text-white" size={28} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-emerald-600 transition-colors">
                    {tenant.category}
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    {tenant.description}
                  </p>
                  <button className="inline-flex items-center text-emerald-600 font-medium hover:text-emerald-700 transition-colors">
                    Learn More
                    <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
                
                {/* Decorative gradient */}
                <div className={`absolute -bottom-20 -right-20 w-40 h-40 bg-gradient-to-br ${gradients[index]} opacity-5 rounded-full group-hover:opacity-10 transition-opacity`} />
              </div>
            );
          })}
        </div>

        {/* Call to Action Banner */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 p-10 md:p-14">
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Interested in Leasing?
              </h3>
              <p className="text-slate-300 max-w-xl">
                Prime retail space is available for qualified tenants. Contact our leasing team
                to discuss your business requirements.
              </p>
            </div>
            <button
              onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="flex-shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-xl font-semibold transition-colors shadow-lg shadow-emerald-600/25"
            >
              Contact Leasing Team
            </button>
          </div>
          
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-teal-500/10 rounded-full blur-3xl" />
        </div>
      </div>
    </section>
  );
};

export default TenantMixSection;
